import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Accessibility from './Accessibility';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faUniversalAccess } from '@fortawesome/free-solid-svg-icons';

const Footer: React.FC = () => {
  const [showAccessibility, setShowAccessibility] = useState(false);
  
  // Toggle accessibility options
  const toggleAccessibility = () => setShowAccessibility(!showAccessibility);

  return (
    <footer className="bg-sage-800 text-cream-50 texture-bg border-t border-sage-700">
      <div className="container-custom py-16">
        {/* Top section with logo and description */}
        <div className="mb-12 pb-10 border-b border-sage-700/50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
            <div>
              <Link to="/" className="flex items-center space-x-3 group mb-5">
                <div className="text-sage-400 transition-colors duration-300 group-hover:text-sage-300">
                  <FontAwesomeIcon icon={faShoppingCart} className="h-7 w-7" />
                </div>
                <span className="text-2xl font-display font-light text-cream-50">GoodCart</span>
              </Link>
              <p className="text-base text-cream-200 leading-relaxed max-w-md">
                Thoughtfully curating health-conscious products that honor both your wellbeing and the earth.
              </p>
            </div>
            <div className="flex flex-col justify-center">
              <button
                className="text-sm text-cream-200 hover:text-cream-50 flex items-center bg-transparent border-0 p-0 cursor-pointer transition-colors duration-300 mb-4"
                onClick={toggleAccessibility}
              >
                <FontAwesomeIcon icon={faUniversalAccess} className="h-4 w-4 mr-2" />
                Accessibility Options
              </button>
              <p className="text-xs text-cream-300 font-light">
                © {new Date().getFullYear()} GoodCart. All rights reserved.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom section with links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {/* Shop Links */}
          <div>

            <h3 className="text-sm font-display font-normal text-cream-50 tracking-wide mb-4">Shop</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/categories" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  Categories
                </Link>
              </li>
              <li>
                <Link to="/search" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  Browse All
                </Link>
              </li>
              <li>
                <Link to="/search?category=organic-foods" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  Organic Foods
                </Link>
              </li>
              <li>
                <Link to="/search?category=natural-supplements" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  Natural Supplements
                </Link>
              </li>
              {/* <li>
                <Link to="/badge-validation" className="text-sm text-gray-600 hover:text-brand-green-600">
                  Verify Badges
                </Link>
              </li>
              <li>
                <Link to="/badge-integration" className="text-sm text-gray-600 hover:text-brand-green-600">
                  Badge Integration
                </Link>
              </li>
              <li>
                <Link to="/badge-designs" className="text-sm text-gray-600 hover:text-brand-green-600">
                  Badge Designs
                </Link>
              </li> */}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-sm font-display font-normal text-cream-50 tracking-wide mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  About
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  Terms of Service
                </Link>
              </li>
              {/* <li>
                <Link to="/badge-admin" className="text-sm text-gray-600 hover:text-brand-green-600">
                  Badge Administration
                </Link>
              </li> */}
              <li>
                <Link to="/badge-application" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  Apply for Badge
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-sm font-display font-normal text-cream-50 tracking-wide mb-4">Resources</h3>
            <ul className="space-y-3">
              <li>
                <a href="/badge-validation" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  Badge Verification
                </a>
              </li>
              <li>
                <a href="/badge-integration" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300">
                  Integration Guide
                </a>
              </li>
              <li>
                <a href="https://x.com/goodcartorg" target="_blank" rel="noopener noreferrer" className="text-sm text-cream-200 hover:text-cream-50 transition-colors duration-300 inline-flex items-center gap-1.5">
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  @goodcartorg
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-display font-normal text-cream-50 tracking-wide mb-4">Disclosure</h3>
            <p className="text-xs text-cream-300 leading-relaxed">
              Affiliate disclosure: we earn a commission on qualifying purchases. Product ratings from public sources. Independent service.
            </p>
          </div>
        </div>
      </div>

      {/* Accessibility component with isOpen state */}
      <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
        <Accessibility isOpen={showAccessibility} onClose={() => setShowAccessibility(false)} />
      </div>
    </footer>
  );
};

export default Footer;